/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { api, mutatingPost, type ApiResult } from "./client";

const BASE = "/v1/admin/insurance";

/** Mirror of nexus core.insurance.AliasSource: where a raw plan string comes from. */
export type AliasSource = "HEALTHIE" | "TYPEFORM" | "SOHAR" | "LEGACY_FEE_SCHEDULE" | "MANUAL";

/** Mirror of nexus core.insurance.Payer: an insurer we know about. */
export type Payer = {
  id: string;
  cmsPayerId: string;
  displayName: string;
  inNetwork: boolean;
};

/** Mirror of nexus core.scheduling.PiTagMapping: what a provider tag grants. */
export type TagMapping = { tagName: string; payer: { value: string }; state: string };

/** Mirror of nexus core.insurance.PayerAlias: what a source's raw string means. */
export type Alias = {
  id: string;
  payerId: string;
  source: AliasSource;
  rawString: string;
  state: string | null;
};

/** Mirror of nexus InsuranceMappingService.Insurer. */
export type Insurer = {
  payer: Payer;
  tags: TagMapping[];
  aliases: Alias[];
  planNames: Record<string, string>;
  poolFamilies: number;
};

/** Mirror of nexus InsuranceGaps.TagGap. */
export type TagGap = { tagName: string; providers: string[]; suggestedState: string | null };

/** Mirror of nexus InsuranceGaps.PlanGap. */
export type PlanGap = {
  planId: string;
  payerName: string | null;
  rawPayerId: string | null;
  families: number;
  poolFamilies: number;
  states: string[];
};

/** Mirror of nexus InsuranceGaps.Report. */
export type InsuranceGaps = { tags: TagGap[]; plans: PlanGap[]; poolFamiliesBlocked: number };

/** Mirror of nexus PatientInsuranceRefresh.Outcome: what re-applying the mappings changed. */
export type RefreshOutcome = { examined: number; changed: number; failed: number };

export type InsurerAdded = { payer: Payer; refreshed: RefreshOutcome | null };
export type NetworkChanged = { payer: Payer; poolFamilies: number };
export type AliasChanged = { alias: Alias; refreshed: RefreshOutcome | null };

export type NewInsurer = { displayName: string; payerId: string; inNetwork: boolean };
export type NewTagMapping = { tagName: string; payerId: string; state: string };
export type NewAlias = {
  payerId: string;
  source: AliasSource;
  rawString: string;
  state: string | null;
};

/** How many tags and plans still need mapping: the Insurance nav badge. */
export function gapCount(gaps: InsuranceGaps): number {
  return gaps.tags.length + gaps.plans.length;
}

export function insuranceGaps(): Promise<ApiResult<InsuranceGaps>> {
  return api<InsuranceGaps>(`${BASE}/gaps`);
}

export function insurers(): Promise<ApiResult<Insurer[]>> {
  return api<Insurer[]>(`${BASE}/insurers`);
}

export function addInsurer(
  insurer: NewInsurer,
  idempotencyKey: string,
): Promise<ApiResult<InsurerAdded>> {
  return api<InsurerAdded>(`${BASE}/insurers`, mutatingPost(insurer, idempotencyKey));
}

export function setInNetwork(payerId: string, inNetwork: boolean): Promise<ApiResult<NetworkChanged>> {
  return api<NetworkChanged>(`${BASE}/insurers/${encodeURIComponent(payerId)}`, {
    method: "PATCH",
    body: JSON.stringify({ inNetwork }),
  });
}

export function mapTag(
  mapping: NewTagMapping,
  idempotencyKey: string,
): Promise<ApiResult<TagMapping>> {
  return api<TagMapping>(`${BASE}/tag-mappings`, mutatingPost(mapping, idempotencyKey));
}

export function unmapTag(tagName: string): Promise<ApiResult<TagMapping>> {
  return api<TagMapping>(`${BASE}/tag-mappings?tagName=${encodeURIComponent(tagName)}`, {
    method: "DELETE",
  });
}

export function addAlias(alias: NewAlias, idempotencyKey: string): Promise<ApiResult<AliasChanged>> {
  return api<AliasChanged>(`${BASE}/aliases`, mutatingPost(alias, idempotencyKey));
}

export function removeAlias(aliasId: string): Promise<ApiResult<AliasChanged>> {
  return api<AliasChanged>(`${BASE}/aliases/${encodeURIComponent(aliasId)}`, { method: "DELETE" });
}
